import React, { Component } from "react";
import { withRouter } from "react-router-dom";

const CUSTOMER_THEME_COLOR = "#100E1E";
const DELIVERY_THEME_COLOR = "#ffffff";

/** Routes that intentionally use a white page canvas (overscroll must match). */
function isLightCanvasPath(pathname) {
  if (!pathname) return false;
  if (pathname.startsWith("/delivery")) return true;
  // Relay / relax product browsing surfaces are white
  if (
    pathname.startsWith("/relax/") ||
    pathname.startsWith("/phase2/") ||
    pathname === "/relax" ||
    pathname === "/phase2"
  ) {
    // Home shells under relax stay dark in Sprint; only category/product white pages
    if (
      pathname === "/relax" ||
      pathname === "/relax/home" ||
      pathname === "/phase2" ||
      pathname === "/phase2/home"
    ) {
      return false;
    }
    return true;
  }
  return false;
}

class ThemeColorController extends Component {
    componentDidMount() {
        this.updateThemeColor();
    }

    componentDidUpdate(prevProps) {
        if (this.props.location !== prevProps.location) {
            this.updateThemeColor();
        }
    }

    componentWillUnmount() {
        document.documentElement.classList.remove("app-canvas-light", "app-canvas-dark");
        document.body.classList.remove("app-canvas-light", "app-canvas-dark");
    }

    applyCanvasBackground = (color, light) => {
        const root = document.documentElement;
        const body = document.body;
        if (!root || !body) return;

        root.classList.toggle("app-canvas-light", light);
        root.classList.toggle("app-canvas-dark", !light);
        body.classList.toggle("app-canvas-light", light);
        body.classList.toggle("app-canvas-dark", !light);

        root.style.setProperty("--app-canvas-bg", color);

        // Keep inline style in sync so pages that clear background still inherit via class,
        // and iOS overscroll uses a solid color (gradients on body are flaky while bouncing).
        root.style.backgroundColor = color;
        // Only set body when a page hasn't installed its own background image/gradient.
        const bodyBgImage = body.style.backgroundImage;
        if (!bodyBgImage || bodyBgImage === "none" || bodyBgImage === "") {
            body.style.backgroundColor = color;
        }
    };

    updateThemeColor = () => {
        const { location } = this.props;
        if (!location) return;

        // Function to apply color
        const applyColor = () => {
            let color = CUSTOMER_THEME_COLOR;
            const pathname = location.pathname;
            let light = isLightCanvasPath(pathname);

            if (pathname.startsWith("/delivery")) {
                color = DELIVERY_THEME_COLOR;
                light = true;
            } else if (pathname === "/login" || pathname === "/otp" || pathname === "/splashscreen" || pathname === "/landing") {
                color = CUSTOMER_THEME_COLOR;
                light = false;
            } else if (
                pathname === "/" ||
                pathname === "/home" ||
                pathname === "/relax" ||
                pathname === "/relax/home" ||
                pathname === "/phase2/home" ||
                pathname.startsWith("/sprint") ||
                pathname === "/my-favorite-stores" ||
                pathname === "/my-wishlist" ||
                pathname.startsWith("/my-wishlist") ||
                pathname === "/best-selling-items" ||
                pathname === "/recommendations" ||
                pathname === "/cart" ||
                pathname.startsWith("/cart") ||
                pathname === "/my-account" ||
                pathname.startsWith("/my-account") ||
                pathname === "/account" ||
                pathname.startsWith("/account")
            ) {
                // Cart is dark in Sprint and white in Relax — prefer dark canvas as safe default;
                // page root paints its own background. Overscroll matches brand dark.
                color = CUSTOMER_THEME_COLOR;
                light = false;
            } else if (light) {
                color = "#ffffff";
            } else {
                // For other pages, try to get header background color
                const header = document.querySelector("header") ||
                    document.querySelector(".storehead-container") ||
                    document.querySelector(".search-location-header");

                if (header) {
                    const styles = getComputedStyle(header);
                    if (styles.backgroundColor && styles.backgroundColor !== 'rgba(0, 0, 0, 0)' && styles.backgroundColor !== 'transparent') {
                        color = styles.backgroundColor;
                    }
                }
            }

            this.applyCanvasBackground(color, light);

            // Triple Tag Strategy:
            // 1. Generic tag
            // 2. Light mode tag
            // 3. Dark mode tag
            // Setting ALL of them to the target color prevents any browser fallback

            const updateOrAddMeta = (selector, media) => {
                let meta = document.querySelector(selector);
                if (!meta) {
                    meta = document.createElement('meta');
                    meta.name = "theme-color";
                    if (media) meta.media = media;
                    document.head.appendChild(meta);
                }
                meta.setAttribute("content", color);
            };

            // 1. Update generic tag (remove media/selectors if found to ensure it's generic)
            const genericMeta = document.querySelector("meta[name='theme-color']:not([media])");
            if (genericMeta) {
                genericMeta.setAttribute("content", color);
            } else {
                // If we only have media tags, create a generic one
                const newMeta = document.createElement('meta');
                newMeta.name = "theme-color";
                newMeta.setAttribute("content", color);
                document.head.appendChild(newMeta);
            }

            // 2. Force specific Light mode tag
            updateOrAddMeta("meta[name='theme-color'][media='(prefers-color-scheme: light)']", "(prefers-color-scheme: light)");

            // 3. Force specific Dark mode tag
            updateOrAddMeta("meta[name='theme-color'][media='(prefers-color-scheme: dark)']", "(prefers-color-scheme: dark)");

            // Also clean up any other conflicting tags if they exist (duplicates)
            const allMetas = document.querySelectorAll("meta[name='theme-color']");
            allMetas.forEach(m => m.setAttribute("content", color));
        };

        // Run immediately
        applyColor();

        // Retry a few times to account for DOM rendering delays
        setTimeout(applyColor, 50);
        setTimeout(applyColor, 200);
        setTimeout(applyColor, 500);
        setTimeout(applyColor, 1000); // Extended retry for slower page transitions
    };

    render() {
        return null; // This component doesn't render anything visible
    }
}

export default withRouter(ThemeColorController);
